<template>
	<panel :show="show" class="verify-caller-id-section" large @close="close">
		<template #title>
			{{ strings.title }}
		</template>
		<template>
			<!-- Step 1: Setup form -->
			<form
				v-if="step === STEPS.SETUP"
				class="verify-caller-id-section__form"
				@submit.prevent="onPlaceVerificationCall"
			>
				<p class="verify-caller-id-section__intro">
					{{ strings.intro }}
				</p>

				<div class="verify-caller-id-section__field">
					<label
						class="verify-caller-id-section__label"
						for="verify-caller-id-number"
					>
						{{ strings.fields.officeNumberLabel }}
					</label>
					<input
						id="verify-caller-id-number"
						v-model="rawPanelPhoneNumber"
						type="text"
						class="verify-caller-id-section__input-readonly"
						:placeholder="placeholders.phoneNumber"
						readonly
					/>
				</div>

				<div class="verify-caller-id-section__field">
					<label
						class="verify-caller-id-section__label"
						for="verify-caller-id-extension"
					>
						{{ strings.fields.extensionCodeLabel }}
					</label>
					<input
						id="verify-caller-id-extension"
						v-model="extensionCode"
						type="text"
						class="verify-caller-id-section__input"
						:placeholder="strings.placeholders.extensionCode"
					/>
					<p v-if="extensionCodeErrorMessage">
						<span class="verify-caller-id-section__field-error">
							{{ extensionCodeErrorMessage }}
						</span>
					</p>
					<p class="verify-caller-id-section__field-help">
						{{ strings.extensionHelp }}
						<br />{{ strings.extensionHelpExample }}
					</p>
				</div>

				<div class="verify-caller-id-section__callout">
					<p class="verify-caller-id-section__callout-title">
						{{ strings.callout.title }}
					</p>
					<p class="verify-caller-id-section__callout-body">
						{{ strings.callout.body }}
					</p>
				</div>

				<div class="verify-caller-id-section__actions">
					<button
						type="button"
						class="verify-caller-id-section__btn verify-caller-id-section__btn--secondary"
						@click="close"
					>
						{{ common.cancel }}
					</button>
					<button
						type="submit"
						class="verify-caller-id-section__btn verify-caller-id-section__btn--primary"
						:disabled="
							isPlacingCall ||
								!canPlaceVerificationCall ||
								Boolean(extensionCodeErrorMessage)
						"
					>
						{{
							isPlacingCall
								? strings.actions.placingCall
								: strings.actions.placeVerificationCall
						}}
					</button>
				</div>
			</form>

			<!-- Step 2+: In-call / timeout / success -->
			<div v-else class="verify-caller-id-section__call-view">
				<div
					class="verify-caller-id-section__status-banner"
					:class="statusBannerClass"
					role="status"
					aria-live="polite"
				>
					{{ statusBannerText }}
				</div>

				<div
					v-if="step !== STEPS.SUCCESS"
					class="verify-caller-id-section__timer-row"
				>
					<span class="verify-caller-id-section__timer-label">
						{{ strings.timerLabel }}
					</span>
					<span
						class="verify-caller-id-section__timer-value"
						:class="{
							'verify-caller-id-section__timer-value--danger':
								step === STEPS.TIMEOUT,
						}"
					>
						{{ formattedElapsedTime }}
					</span>
				</div>

				<div
					v-if="step !== STEPS.SUCCESS"
					class="verify-caller-id-section__pin-box"
					:class="{
						'verify-caller-id-section__pin-box--timeout':
							step === STEPS.TIMEOUT,
					}"
				>
					<p class="verify-caller-id-section__pin-label">
						{{ strings.pinLabel }}
					</p>
					<div
						class="verify-caller-id-section__pin-digits"
						:aria-label="strings.pinAriaLabel"
					>
						<span
							v-for="(digit, index) in pinDigits"
							:key="index"
							class="verify-caller-id-section__pin-digit"
						>
							{{ digit }}
						</span>
					</div>
				</div>

				<div
					v-if="step === STEPS.SUCCESS"
					class="verify-caller-id-section__success-box"
				>
					<div class="verify-caller-id-section__success-text">
						{{ strings.success.intro }}
						<div>
							<strong
								class="verify-caller-id-section__success-number"
							>
								{{ formattedPanelPhoneNumber }}
							</strong>
						</div>
						{{ strings.success.outro }}
					</div>
				</div>

				<p
					v-if="step === STEPS.CALLING || step === STEPS.TIMEOUT"
					class="verify-caller-id-section__instruction"
				>
					{{ strings.instruction }}
				</p>

				<div
					v-if="step === STEPS.TIMEOUT"
					class="verify-caller-id-section__connection-error"
					role="alert"
				>
					{{ strings.connectionError }}
				</div>

				<div
					class="verify-caller-id-section__actions verify-caller-id-section__actions--center"
				>
					<button
						type="button"
						class="verify-caller-id-section__btn verify-caller-id-section__btn--secondary"
						@click="close"
					>
						{{
							step === STEPS.SUCCESS
								? strings.actions.done
								: step === STEPS.TIMEOUT
								? strings.actions.close
								: common.cancel
						}}
					</button>
				</div>
			</div>
		</template>
	</panel>
</template>

<script>
import Panel from './Panel'
import PanelMixin from '@/mixins/PanelMixin'
import util from '@/util'
import { mapActions, mapGetters } from 'vuex'
import {
	SETTINGS_COMMON,
	SETTINGS_PLACEHOLDERS,
	VERIFY_CALLER_ID_STEPS,
	VERIFY_CALLER_ID_STATUS_POLL_INTERVAL_MS,
	VERIFY_CALLER_ID_STRINGS,
	VERIFY_CALLER_ID_TIMEOUT_SECONDS,
} from '@/config/settingsConstants'

export default {
	components: {
		Panel,
	},

	computed: {
		...mapGetters({
			partner: 'partners/active',
		}),

		canPlaceVerificationCall() {
			return (
				Boolean(this.panelPhoneNumber) && !this.panelPhoneErrorMessage
			)
		},

		formattedElapsedTime() {
			const minutes = Math.floor(this.elapsedSeconds / 60)
			const seconds = this.elapsedSeconds % 60
			return `${minutes}:${String(seconds).padStart(2, '0')}`
		},

		formattedPanelPhoneNumber() {
			return util.newFormatPhoneNumberOMD(this.panelPhoneNumber)
		},

		extensionCodeErrorMessage() {
			const code = this.extensionCode

			if (!code) {
				return ''
			}

			const isValid = /^[0-9w*#]+$/.test(code)

			if (!isValid) {
				return this.strings.validation.invalidExtensionCode
			}

			return ''
		},

		pinDigits() {
			return String(this.verificationPin || '')
				.split('')
				.filter(Boolean)
		},

		rawPanelPhoneNumber: {
			get() {
				return util.newFormatPhoneNumberOMD(this.panelPhoneNumber)
			},
			set(value) {
				this.panelPhoneNumber = util.removeNonNumericCharsOMD(value)
			},
		},

		statusBannerClass() {
			return {
				'verify-caller-id-section__status-banner--connecting':
					this.step === VERIFY_CALLER_ID_STEPS.CALLING,
				'verify-caller-id-section__status-banner--timeout':
					this.step === VERIFY_CALLER_ID_STEPS.TIMEOUT,
				'verify-caller-id-section__status-banner--success':
					this.step === VERIFY_CALLER_ID_STEPS.SUCCESS,
			}
		},

		statusBannerText() {
			if (this.step === VERIFY_CALLER_ID_STEPS.CALLING) {
				return this.strings.status.connecting
			}

			if (this.step === VERIFY_CALLER_ID_STEPS.TIMEOUT) {
				return this.strings.status.timeout
			}

			if (this.step === VERIFY_CALLER_ID_STEPS.SUCCESS) {
				return this.strings.status.verified
			}

			return ''
		},
	},

	methods: {
		close() {
			this.stopElapsedTimer()
			this.stopVerificationStatusPolling()
			this.$emit('close')
		},

		resetForm() {
			this.stopElapsedTimer()
			this.stopVerificationStatusPolling()
			this.step = VERIFY_CALLER_ID_STEPS.SETUP
			this.panelPhoneNumber = this.getSanitizedPhoneNumber(
				this.phoneNumber
			)
			this.extensionCode = ''
			this.panelPhoneErrorMessage = ''
			this.verificationPin = ''
			this.elapsedSeconds = 0
			this.isPlacingCall = false
		},

		getSanitizedPhoneNumber(value) {
			if (!value) {
				return ''
			}

			return String(value)
				.replace(/^\+1/, '')
				.replace(/\D/g, '')
		},

		startElapsedTimer() {
			this.stopElapsedTimer()
			this.elapsedSeconds = 0

			this.elapsedTimerId = window.setInterval(() => {
				this.elapsedSeconds += 1

				if (
					this.step === VERIFY_CALLER_ID_STEPS.CALLING &&
					this.elapsedSeconds >= VERIFY_CALLER_ID_TIMEOUT_SECONDS
				) {
					this.onVerificationTimeout()
				}
			}, 1000)
		},

		stopElapsedTimer() {
			if (this.elapsedTimerId) {
				window.clearInterval(this.elapsedTimerId)
				this.elapsedTimerId = null
			}
		},

		onVerificationTimeout() {
			this.step = VERIFY_CALLER_ID_STEPS.TIMEOUT
			this.stopVerificationStatusPolling()
			this.stopElapsedTimer()
		},

		stopVerificationStatusPolling() {
			if (this.verificationStatusIntervalId) {
				clearInterval(this.verificationStatusIntervalId)
				this.verificationStatusIntervalId = null
			}
		},

		getVerificationStatusFromResponse(response) {
			const partnerData = response?.get?.('data', {}) || {}

			return (
				partnerData.ob_caller_id_verification_status ||
				this.partner?.ob_caller_id_verification_status ||
				''
			)
		},

		async pollVerificationStatus(partnerId) {
			if (this.step !== VERIFY_CALLER_ID_STEPS.CALLING) {
				return
			}

			const response = await this.getPartner(partnerId)
			const status = String(
				this.getVerificationStatusFromResponse(response)
			)
				.trim()
				.toLowerCase()

			if (status === 'verified') {
				this.stopVerificationStatusPolling()
				this.onVerificationSuccess()
			}
		},

		async checkVerificationStatus() {
			const partnerId = parseInt(this.$route.params.partner, 10)
			if (!partnerId) {
				return
			}

			this.stopVerificationStatusPolling()
			await this.pollVerificationStatus(partnerId)

			this.verificationStatusIntervalId = window.setInterval(() => {
				this.pollVerificationStatus(partnerId)
			}, VERIFY_CALLER_ID_STATUS_POLL_INTERVAL_MS)
		},

		async onPlaceVerificationCall() {
			this.isPlacingCall = true
			this.$emit('phone-number-change', this.panelPhoneNumber)

			try {
				const response = await this.verifyOutboundCallerId({
					phoneNumber: this.panelPhoneNumber,
					extensionCode: this.extensionCode,
				})
				if (response.status === 201) {
					this.verificationPin =
						response.response.data.validation_code
					this.step = VERIFY_CALLER_ID_STEPS.CALLING
					this.startElapsedTimer()
					await this.checkVerificationStatus()
				}
			} catch (error) {
				this.$alert.error(
					error?.response?.message ||
						this.strings.errors.placeCallFailed
				)
			} finally {
				this.isPlacingCall = false
			}
		},

		onVerificationSuccess() {
			this.stopVerificationStatusPolling()
			this.stopElapsedTimer()
			this.step = VERIFY_CALLER_ID_STEPS.SUCCESS
			this.$emit('verified', this.panelPhoneNumber)
		},

		...mapActions({
			getPartner: 'partners/find',
			verifyOutboundCallerId: 'auth/verifyOutboundCallerId',
		}),
	},

	mixins: [PanelMixin],

	name: 'VerifyCallerIdPanel',

	props: {
		phoneNumber: {
			type: String,
			default: '',
		},
	},

	watch: {
		phoneNumber: {
			immediate: true,
			handler(value) {
				if (this.step === VERIFY_CALLER_ID_STEPS.SETUP) {
					this.panelPhoneNumber = this.getSanitizedPhoneNumber(value)
				}
			},
		},

		show(visible) {
			if (visible) {
				this.resetForm()
			} else {
				this.stopVerificationStatusPolling()
				this.stopElapsedTimer()
			}
		},
	},

	beforeDestroy() {
		this.stopElapsedTimer()
		this.stopVerificationStatusPolling()
	},

	data() {
		return {
			STEPS: VERIFY_CALLER_ID_STEPS,
			common: SETTINGS_COMMON,
			strings: VERIFY_CALLER_ID_STRINGS,
			placeholders: SETTINGS_PLACEHOLDERS,
			step: VERIFY_CALLER_ID_STEPS.SETUP,
			panelPhoneNumber: '',
			extensionCode: '',
			panelPhoneErrorMessage: '',
			verificationPin: '',
			elapsedSeconds: 0,
			elapsedTimerId: null,
			verificationStatusIntervalId: null,
			isPlacingCall: false,
		}
	},
}
</script>
